// Thin fetch wrapper. Uses cookie auth (credentials: include for cross-origin dev,
// same-origin in prod). Throws ApiError on non-2xx with parsed body.

export class ApiError extends Error {
  constructor(
    public status: number,
    public body: unknown,
    message?: string,
  ) {
    super(message ?? `HTTP ${status}`);
  }
}

const baseInit: RequestInit = {
  credentials: 'include',
  headers: { 'Accept': 'application/json' },
};

export function structureReportPath(id: string | number, fresh = false) {
  return `/structurereport/view/${id}${fresh ? '?fresh=1' : ''}`;
}

async function request<T>(method: string, url: string, body?: unknown): Promise<T> {
  const init: RequestInit = {
    ...baseInit,
    method,
    headers: {
      ...(baseInit.headers as Record<string, string>),
      ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
    },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  };

  const res = await fetch(url, init);
  if (res.status === 204) return undefined as T;

  const contentType = res.headers.get('content-type') ?? '';
  const data = contentType.includes('application/json')
    ? await res.json()
    : await res.text();

  if (!res.ok) {
    throw new ApiError(res.status, data);
  }
  return data as T;
}

export const api = {
  get: <T>(url: string) => request<T>('GET', url),
  post: <T>(url: string, body?: unknown) => request<T>('POST', url, body),
  put: <T>(url: string, body?: unknown) => request<T>('PUT', url, body),
  del: <T>(url: string) => request<T>('DELETE', url),
};

// ---- shared types ----

export interface AuthUser {
  id: number;
  name: string;
  email: string | null;
  companyName: string | null;
  customerType: string | null;
  isSuspended: boolean;
  wasGuest: boolean;
  attributeId: number;
  roles: string[];
  emailVerified: boolean;
  homeTour: boolean;
}

export interface AccountProfileDto {
  id: number;
  name: string;
  email: string | null;
  phone: string | null;
  company: string | null;
  country: string | null;
  state: string | null;
  city: string | null;
  county: string | null;
  zipCode: string | null;
  customerType: string | null;
  optInNewsletter: boolean;
}

export interface PagedResult<T> {
  items: T[];
  page: number;
  pageSize: number;
  total: number;
}

export interface StructureListItem {
  id: number;
  hashId: string;
  nickname: string | null;
  structureTypeId: number | null;
  structureTypeName: string | null;
  grateStyleId: number | null;
  grateStyleName: string | null;
  styleNumber: string | null;
  foundryPartNumber: string | null;
  foundryId: number | null;
  foundryName: string | null;
  isDraft: boolean;
  isCompleted: boolean;
  quoteRequested: string | null;
  shared: string | null;
  submitted: string | null;
  downloaded: string | null;
  lastViewed: string | null;
  createdAt: string | null;
  updatedAt: string | null;
  permanent: boolean;
  oilFiltration: boolean;
  frameClearOpeningDiameter: number | null;
  clearOpeningLeftToRight: number | null;
  clearOpeningFrontToBack: number | null;
  clearOpeningLongSpan: number | null;
  clearOpeningShortSpan: number | null;
  curbOpeningWidth: number | null;
  basinSize: number | null;
}

export interface ProjectFolderListItem {
  id: number;
  name: string;
  city: string;
  state: string;
  designEngineer: string | null;
  siteContractor: string | null;
  completedStatus: boolean;
  deliveredStatus: boolean;
  requestedSalesContact: string | null;
  shared: string | null;
  quoteCompleted: string | null;
  structureCount: number;
  totalStructures: number;
  createdAt: string | null;
  updatedAt: string | null;
}

// ---- Lookups ----

export interface StructureType {
  id: number;
  name: string;
  family: string;
  abbreviation: string | null;
  is4SeatSquare: boolean;
  is2SeatLong: boolean;
  is2SeatShort: boolean;
  isConcrete: boolean;
  isCastIron: boolean;
  isCastIronRound: boolean;
  isCastIronRectangular: boolean;
  isCastIronCurbBoxCombo: boolean;
  isOpenThroat: boolean;
  isNyloplast: boolean;
  enabledFields: string;
  extraOutputs: string;
}

export interface GrateStyle {
  id: number;
  structureTypeId: number | null;
  name: string;
  basinSize: number | null;
  hangerKitTransitionPlate: boolean;
  filterSize: number | null;
}

export interface Foundry {
  id: number;
  name: string;
}

export interface FoundryPart {
  id: number;
  foundryId: number | null;
  partId: number | null;
  foundryPartNumber: string;
  partNumberTemporary: string | null;
  partNumberPermanentNoFilter: string | null;
  partNumberPermanentOilFilter: string | null;
  grateSizeWidth: number | null;
  grateSizeHeight: number | null;
  grateSizeDiameter: number | null;
  openingSizeWidth: number | null;
  openingSizeHeight: number | null;
  openingSizeDiameter: number | null;
  inletType: string;
  abbreviation: string;
  lineNote: string;
}

export interface FoundryPartSearchResult {
  id: number;
  foundryId: number | null;
  foundryName: string | null;
  partId: number | null;
  structureTypeId: number | null;
  structureTypeName: string | null;
  foundryPartNumber: string;
  abbreviation: string;
}

// ---- Structures ----

export interface StructureDetail {
  id: number;
  hashId: string;
  nickname: string | null;
  permanent: boolean;
  oilFiltration: boolean;
  structureTypeId: number | null;
  structureTypeName: string | null;
  grateStyleId: number | null;
  grateStyleName: string | null;
  hangerKitTransitionPlatePartNumber: string | null;
  grateDiameter: number | null;
  frameClearOpeningDiameter: number | null;
  grateSizeLeftToRight: number | null;
  grateSizeFrontToBack: number | null;
  clearOpeningLeftToRight: number | null;
  clearOpeningFrontToBack: number | null;
  grateSizeLongSpan: number | null;
  grateSizeShortSpan: number | null;
  clearOpeningLongSpan: number | null;
  clearOpeningShortSpan: number | null;
  curbOpeningWidth: number | null;
  catchBasinWallToWallInsideDimension: number | null;
  basinSize: number | null;
  extendedBottomHeavyDuty: boolean;
  extendedBottomDepth: number | null;
  extendedBottomWidth: number | null;
  foundryId: number | null;
  foundryName: string | null;
  foundryPartId: number | null;
  foundryPartNumber: string | null;
  foundryPartLineNote: string | null;
  foundryPartAbbreviation: string | null;
  foundryPartGrateSizeWidth: number | null;
  foundryPartGrateSizeHeight: number | null;
  foundryPartGrateSizeDiameter: number | null;
  foundryPartOpeningSizeWidth: number | null;
  foundryPartOpeningSizeHeight: number | null;
  foundryPartOpeningSizeDiameter: number | null;
  partId: number | null;
  partNumber: string | null;
  userId: number;
  userName: string | null;
  structureNotes: string | null;
  contactRepComments: string | null;
  quoteRequested: string | null;
  shared: string | null;
  submitted: string | null;
  downloaded: string | null;
  completed: string | null;
  quoteCompleted: string | null;
  lastViewed: string | null;
  createdAt: string | null;
  updatedAt: string | null;
  isDraft: boolean;
  projectFolderIds: number[];
}

export interface StructureWrite {
  nickname?: string | null;
  permanent: boolean;
  oilFiltration: boolean;
  structureTypeId?: number | null;
  grateStyleId?: number | null;
  grateDiameter?: number | null;
  frameClearOpeningDiameter?: number | null;
  grateSizeLeftToRight?: number | null;
  grateSizeFrontToBack?: number | null;
  clearOpeningLeftToRight?: number | null;
  clearOpeningFrontToBack?: number | null;
  grateSizeLongSpan?: number | null;
  grateSizeShortSpan?: number | null;
  clearOpeningLongSpan?: number | null;
  clearOpeningShortSpan?: number | null;
  curbOpeningWidth?: number | null;
  catchBasinWallToWallInsideDimension?: number | null;
  basinSize?: number | null;
  extendedBottomHeavyDuty: boolean;
  extendedBottomDepth?: number | null;
  extendedBottomWidth?: number | null;
  foundryId?: number | null;
  foundryPartId?: number | null;
  structureNotes?: string | null;
  isDraft: boolean;
}

export interface CastingLookupInput {
  structureTypeId: number;
  permanent: boolean;
  oilFiltration: boolean;
  extendedBottomHeavyDuty: boolean;
  grateStyleId?: number | null;
  clearOpeningLeftToRight?: number | null;
  clearOpeningFrontToBack?: number | null;
  clearOpeningLongSpan?: number | null;
  clearOpeningShortSpan?: number | null;
  frameClearOpeningDiameter?: number | null;
  curbOpeningWidth?: number | null;
  basinSize?: number | null;
}

export interface CastingMatch {
  partId: number;
  partNumberPermanentOilFilter: string | null;
  partNumberPermanentNoFilter: string | null;
  partNumberTemporary: string | null;
  openingSizeWidth: number | null;
  openingSizeLength: number | null;
  openingSizeDiameter: number | null;
  inletTypeId: number | null;
  inletTypeName: string | null;
}

// ---- Project folders ----

export interface ProjectFolderStructure {
  id: number;
  nickname: string | null;
  structureTypeId: number | null;
  structureTypeName: string | null;
  foundryId: number | null;
  foundryName: string | null;
  permanent: boolean;
  oilFiltration: boolean;
  isDraft: boolean;
  quantity: number;
  styleNumber: string | null;
  foundryPartNumber: string | null;
  foundryPartLineNote: string | null;
  foundryPartGrateSizeWidth: number | null;
  foundryPartGrateSizeHeight: number | null;
  foundryPartGrateSizeDiameter: number | null;
  foundryPartOpeningSizeWidth: number | null;
  foundryPartOpeningSizeHeight: number | null;
  foundryPartOpeningSizeDiameter: number | null;
  grateDiameter: number | null;
  frameClearOpeningDiameter: number | null;
  grateSizeLeftToRight: number | null;
  grateSizeFrontToBack: number | null;
  clearOpeningLeftToRight: number | null;
  clearOpeningFrontToBack: number | null;
  grateSizeLongSpan: number | null;
  grateSizeShortSpan: number | null;
  clearOpeningLongSpan: number | null;
  clearOpeningShortSpan: number | null;
  curbOpeningWidth: number | null;
  catchBasinWallToWallInsideDimension: number | null;
  basinSize: number | null;
  hangerKitTransitionPlatePartNumber: string | null;
}

export interface ProjectFolderDetail {
  id: number;
  userId: number;
  userName: string | null;
  name: string;
  designEngineer: string | null;
  siteContractor: string | null;
  city: string;
  state: string;
  completedStatus: boolean;
  deliveredStatus: boolean;
  requestedSalesContact: string | null;
  shared: string | null;
  submitted: string | null;
  downloaded: string | null;
  quoteCompleted: string | null;
  contactRepComments: string | null;
  additionalNotes: string | null;
  createdAt: string | null;
  updatedAt: string | null;
  structures: ProjectFolderStructure[];
}

export interface ProjectFolderWrite {
  name: string;
  city: string;
  state: string;
  designEngineer?: string | null;
  siteContractor?: string | null;
  additionalNotes?: string | null;
}

// ---- Cost engine ----

export const PIPE_SIZES = [12, 15, 18, 24, 30, 36, 42, 48, 60] as const;

export interface CostSetupDto {
  adsStickLength20: number;
  adsStickLength13: number;
  otherStickLength: number;
  adsCrewCost: number;
  otherCrewCost: number;
  adsTimePerTruckMinutes: number;
  adsLaborRate: number;
  otherTimePerTruckMinutes: number;
  otherLaborRate: number;
  useNestedTruckloads: boolean;
}

export interface PipeRunDto {
  stickLengthFt: number;
  totalLengthFt: number;
  adsCostPerFoot: number;
  adsTruckloadFt: number;
  adsSticksPerTruck: number;
  adsMinParentSize: number;
}

export interface PipeDto {
  sizeInches: number;
  adsInstallRate: number;
  otherInstallRate: number;
  otherCostPerFoot: number;
  otherTruckloadFt: number;
  runs: PipeRunDto[];
}

export interface BackfillLayerDto {
  cost: number;
  density: number;
  wastePct: number;
  isNative: boolean;
}

export interface BackfillGeometryDto {
  sizeInches: number;
  outerDiameter: number;
  trenchWidth: number;
  totalLength: number;
  laidBackBedding: number;
  laidBackSpringline: number;
  laidBackTopOfPipe: number;
  laidBackSixOver: number;
  laidBackTwelveOver: number;
}

export interface BackfillSetupDto {
  side: 'ads' | 'other';
  product: string;            // 'N-12' | 'HP Storm' | 'RCP' | 'CMP' | 'other'
  unit: string;               // 'Imperial' | 'Metric'
  trenchBoxMode: string;      // 'none' | 'ads' | 'both'
  spoilDisposalRate: number;
  bedding: BackfillLayerDto;
  springline: BackfillLayerDto;
  topOfPipe: BackfillLayerDto;
  sixOver: BackfillLayerDto;
  twelveOver: BackfillLayerDto;
  geometry: BackfillGeometryDto[];
}

export interface CostReportDto {
  pipe: { adsTotal: number; otherTotal: number; savingsTotal: number; reductionPercent: number };
  crew: { adsTotal: number; otherTotal: number; savings: number; reductionPercent: number };
  joints: { adsTotal: number; otherTotal: number };
  days: { adsDays: number; otherDays: number };
  delivery: { adsTrucks: number; otherTrucks: number; adsTotal: number; otherTotal: number };
  adsBackfill: { total: number; bySize: Record<string, number> };
  otherBackfill: { total: number; bySize: Record<string, number> };
  totals: { adsTotal: number; otherTotal: number; savings: number; reductionPercent: number };
}

// ---- Project deliveries (share by email) ----

export interface DeliveryDto {
  id: number;
  projectFolderId: number;
  email: string;
  emailDelivered: boolean;
  deliveredUpdated: boolean;
  createdAt: string | null;
}

export interface ShareProjectRequest {
  sendToAccount: boolean;
  sendToAds: boolean;
  recipients: string[];
  optInNewsletter: boolean;
}

export interface ShareProjectResponse {
  message: string;
  shared: string | null;
  deliveredStatus: boolean;
  completedStatus: boolean;
  requestedSalesContact: string | null;
  recipients: DeliveryDto[];
}

// ---- Structure deliveries ----

export interface StructureDeliveryDto {
  id: number;
  structureId: number;
  email: string;
  emailDelivered: boolean;
  deliveredUpdated: boolean;
  createdAt: string | null;
}

export interface ShareStructureRequest {
  sendToAccount: boolean;
  sendToAds: boolean;
  recipients: string[];
  optInNewsletter: boolean;
}

export interface ShareStructureResponse {
  message: string;
  shared: string | null;
  recipients: StructureDeliveryDto[];
}

// ---- Admin ----

export interface AdminUserListItem {
  id: number;
  name: string;
  email: string | null;
  company: string | null;
  customerType: string | null;
  city: string | null;
  state: string | null;
  loginCount: number;
  suspended: string | null;
  createdAt: string | null;
  roles: string[];
}

export interface AdminUserDetail {
  id: number;
  name: string;
  email: string | null;
  phone: string | null;
  company: string | null;
  customerType: string | null;
  market: string | null;
  country: string | null;
  city: string | null;
  state: string | null;
  county: string | null;
  zipCode: string | null;
  loginCount: number;
  suspended: string | null;
  optinNewsletter: string | null;
  createdAt: string | null;
  updatedAt: string | null;
  projectCount: number;
  structureCount: number;
  roles: string[];
}

export interface AdminUserProject {
  id: number;
  name: string;
  city: string;
  state: string;
  quoteCompleted: string | null;
  submitted: string | null;
  shared: string | null;
  completedStatus: boolean;
  deliveredStatus: boolean;
  createdAt: string | null;
}

export interface AdminUserStructure {
  id: number;
  name: string;
  quoteCompleted: string | null;
  createdAt: string | null;
}

export interface SiteMetricLookupItem {
  id: number;
  name: string;
}

export interface SiteMetricsLookups {
  attributes: SiteMetricLookupItem[];
  customerTypes: SiteMetricLookupItem[];
}

export interface SiteMetricsDailyPoint {
  date: string;
  unfiltered: number;
  active: number;
  guests: number;
}

export interface SiteMetricsResponse {
  from: string;
  to: string;
  attributeId: number | null;
  customerTypeId: number | null;
  unfilteredTraffic: number;
  activeTraffic: number;
  guestSessions: number;
  signupsCount: number;
  guestsConverted: number;
  optInCount: number;
  daily: SiteMetricsDailyPoint[];
  lookups: SiteMetricsLookups;
}

export interface UserReportRow {
  id: number;
  name: string;
  createdAt: string | null;
  loginCount: number;
  reportsMade: number;
  reportsDelivered: number;
  freeQuoteRequests: number;
  newsOptIn: string | null;
  state: string | null;
  suspended: string | null;
}

export interface UsersReportResponse {
  from: string | null;
  to: string | null;
  customerTypeId: number | null;
  attributeId: number | null;
  unfilteredTraffic: number;
  activeTraffic: number;
  totalGuests: number;
  totalGuestsToUsers: number;
  totalUsers: number;
  newsProductOptIns: number;
  users: UserReportRow[];
  lookups: SiteMetricsLookups;
}

export interface ProjectReportRow {
  id: number;
  name: string;
  status: string;
  hasAgentNotes: boolean;
  createdAt: string | null;
  updatedAt: string | null;
  userId: number;
  ownerName: string | null;
}

export interface ProjectsReportResponse {
  from: string | null;
  to: string | null;
  customerTypeId: number | null;
  projectsStarted: number;
  projectsFinished: number;
  projectsUpdated: number;
  totalProjects: number;
  quoteRequests: number;
  projectsDownloaded: number;
  projectsEmailed: number;
  projects: ProjectReportRow[];
  lookups: SiteMetricsLookups;
}

export interface StructureReportRow {
  id: number;
  name: string;
  status: string;
  hasAgentNotes: boolean;
  createdAt: string | null;
  updatedAt: string | null;
  userId: number;
  ownerName: string | null;
}

export interface StructuresReportResponse {
  from: string | null;
  to: string | null;
  customerTypeId: number | null;
  structuresStarted: number;
  structuresFinished: number;
  structuresUpdated: number;
  totalStructures: number;
  quoteRequests: number;
  structuresDownloaded: number;
  structuresEmailed: number;
  structures: StructureReportRow[];
  lookups: SiteMetricsLookups;
}

export interface AdvancedReportBucket {
  customerType: string;
  count: number;
}

export interface AdvancedReportResponse {
  from: string | null;
  to: string | null;
  customerTypeId: number | null;
  totalUsers: number;
  buckets: AdvancedReportBucket[];
  lookups: SiteMetricsLookups;
}

export interface AgentNoteDto {
  id: number;
  note: string;
  userId: number;
  userName: string | null;
  createdAt: string | null;
}

export interface FoundryAdminListItem {
  id: number;
  name: string;
  hidden: boolean;
  partCount: number;
  hiddenPartCount: number;
}

export interface FoundryPartAdmin {
  id: number;
  foundryId: number;
  partId: number | null;
  foundryPartNumber: string;
  partNumberTemporary: string | null;
  partNumberPermanentNoFilter: string | null;
  partNumberPermanentOilFilter: string | null;
  grateSizeWidth: number | null;
  grateSizeHeight: number | null;
  grateSizeDiameter: number | null;
  openingSizeWidth: number | null;
  openingSizeHeight: number | null;
  openingSizeDiameter: number | null;
  inletType: string;
  abbreviation: string;
  lineNote: string | null;
  hidden: boolean;
  hiddenReason: string | null;
}

export interface FoundryDetail {
  id: number;
  name: string;
  hidden: boolean;
  hiddenReason: string | null;
  parts: FoundryPartAdmin[];
}

export interface FoundryWrite {
  name: string;
  hide: boolean;
  hiddenReason: string | null;
}

export interface FoundryPartWrite {
  foundryPartNumber: string;
  partNumberTemporary: string | null;
  partNumberPermanentNoFilter: string | null;
  partNumberPermanentOilFilter: string | null;
  grateSizeWidth: number | null;
  grateSizeHeight: number | null;
  grateSizeDiameter: number | null;
  openingSizeWidth: number | null;
  openingSizeHeight: number | null;
  openingSizeDiameter: number | null;
  inletTypeId: number | null;
  lineNote: string | null;
}

export interface FoundryPartHide {
  hide: boolean;
  hiddenReason: string | null;
}

export interface InletTypeOption {
  id: number;
  name: string;
  abbreviation: string;
}

export interface PardotConfigDto {
  accountCreatedUrl: string | null;
  salesRepUrl: string | null;
  clientId: string | null;
  clientSecret: string | null;
  campaignOverride: string | null;
  batchSize: number | null;
}

export interface PardotConfigResponse {
  effective: PardotConfigDto;
  overrides: PardotConfigDto;
  defaults: PardotConfigDto;
  accountCreatedConfigured: boolean;
  salesRepConfigured: boolean;
}

export interface AdminMetrics {
  totalUsers: number;
  activeUsers: number;
  suspendedUsers: number;
  totalProjects: number;
  totalStructures: number;
  recentSignups7d: number;
  recentProjects7d: number;
  recentStructures7d: number;
  signupsByDay: { date: string; count: number }[];
  projectsByDay: { date: string; count: number }[];
}

export interface StructureTypeAdmin {
  id: number;
  name: string;
  family: string;
  abbreviation: string | null;
  is4SeatSquare: boolean;
  is2SeatLong: boolean;
  is2SeatShort: boolean;
  isConcrete: boolean;
  isCastIron: boolean;
  isCastIronRound: boolean;
  isCastIronRectangular: boolean;
  isCastIronCurbBoxCombo: boolean;
  isOpenThroat: boolean;
  isNyloplast: boolean;
  enabledFields: string;
  extraOutputs: string;
  structureCount: number;
}

export type StructureTypeWrite = Omit<StructureTypeAdmin, 'id' | 'structureCount'>;

export interface JobRun {
  id: number;
  jobName: string;
  startedAt: string;
  finishedAt: string | null;
  success: boolean | null;
  itemsProcessed: number;
  message: string | null;
  trigger: string;
}

export interface JobDefinition {
  name: string;
}

export interface NotificationDto {
  id: number;
  group: string | null;
  bodyHtml: string;
  dismissable: boolean;
  displayAfter: string | null;
  createdAt: string | null;
}

export interface NotificationWriteDto {
  group: string | null;
  bodyHtml: string;
  dismissable: boolean;
  displayAfter: string | null;
}

export type UserBackfillDefaultDto = Omit<BackfillSetupDto, 'geometry'>;

export interface ApplyDefaultsResultDto {
  costSetupApplied: boolean;
  pipesApplied: number;
  backfillSidesApplied: number;
  reserved: number;
}
